import { useEffect } from 'react'

import { ButtonComponent } from '../Button'
import { DealPreviewCardComponent } from '../DealPreviewCard'
import { FieldSelectComponent } from '../FieldSelect'
import { FieldTextComponent } from '../FieldText'
import { FieldToggleGroupComponent } from '../FieldToggleGroup'
import { FormSectionComponent } from '../FormSection'
import { PickerDateComponent } from '../PickerDate'

import { FetchHelper } from '../../helpers/fetch'

import { useSimpleState } from '../../hooks/useSimpleState'

import dayjs from 'dayjs'

import './index.css'

const couponTypeOptions = [
    { value: 0, label: 'None' },
    { value: 1, label: 'Percentage' },
    { value: 2, label: 'Dollar' }
]

const CreateDealFormComponent = ({ value={}, config={}, events={} }) => {
    const {_state} = useSimpleState({
        affiliateId: value.affiliateId || 1,
        url: value.url || '',
        name: value.name || '',
        description: value.description || '',
        date: value.date || dayjs().format('YYYY-MM-DD HH:mm:ss'),
        priceOriginal: value.priceOriginal || '',
        priceFinal: value.priceFinal || '',
        imageUrl: value.imageUrl || '',

        couponRedeemableDescription: value.couponRedeemableDescription || '',
        couponPromoCode: value.couponPromoCode || ''
    })

    const {_state: _clickableCouponState} = useSimpleState({
        selected: getClickableCouponStateSelection(),
        couponClickablePercentage: value.couponClickablePercentage || '',
        couponClickableAmount: value.couponClickableAmount || ''
    })

    const {_state: _affiliates} = useSimpleState({
        fetching: true,
        list: []
    })

    function getClickableCouponStateSelection() {
        if (value.couponClickablePercentage) {
            return 1
        } else if (value.couponClickableAmount) {
            return 2
        } else {
            return 0
        }
    }

    useEffect(() => {
        FetchHelper({ url: '/api/v1/affiliates' }).then((res) => {
            if (res.status === 200)
                _affiliates.set('list', res.body.data)
        }).finally(() => {
            _affiliates.set('fetching', false)
        })
    }, [])

    const handleDateChange = (val) => {
        _state.set('date', val)
    }

    const filterNumber = (val) => {
        return val.replace(/[^0-9\.]+/, '')
    }

    const getSelectedAffiliateName = () => {
        const affiliate = _affiliates.get('list').find((item) => item.id === _state.get('affiliateId'))
        return affiliate?.displayName || affiliate?.name || ''
    }

    const handleSubmit = () => {
        let body = _state.get()

        switch (_clickableCouponState.get('selected')) {
            case 1:
                body.couponClickablePercentage = _clickableCouponState.get('couponClickablePercentage')
                break
            case 2:
                body.couponClickableAmount = _clickableCouponState.get('couponClickableAmount')
                break
        }

        if (config.actionType === 'edit') {
            body.id = value.id
        }

        if (events.handleSubmit)
            events.handleSubmit(body)
    }

    return (
        <div className="c-CreateDealForm">
            <div className="createDealForm-layout">
                <div className="createDealForm-fields">
                    <FormSectionComponent config={{ title: 'Basics' }}>
                        <div className="form-fields">
                            <FieldSelectComponent
                                value={_state.get('affiliateId')}
                                config={{
                                    name: 'affiliateId',
                                    label: 'Affiliate',
                                    errors: config.errors,
                                    helperText: _affiliates.get('fetching') ? 'Loading affiliates…' : undefined,
                                    options: _affiliates.get('list').map((item) => ({
                                        value: item.id,
                                        label: item.displayName || item.name
                                    }))
                                }}
                                events={{ handleChange: (val) => _state.set('affiliateId', val) }}
                            />

                            <FieldTextComponent
                                value={_state.get('name')}
                                config={{ name: 'name', label: 'Name', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('name', val) }}
                            />

                            <FieldTextComponent
                                value={_state.get('url')}
                                config={{ name: 'url', label: 'URL', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('url', val) }}
                            />

                            <FieldTextComponent
                                value={_state.get('description')}
                                config={{ name: 'description', label: 'Description', multiline: true, rows: 3, errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('description', val) }}
                            />

                            <PickerDateComponent
                                value={_state.get('date')}
                                config={{ label: 'Date' }}
                                events={{ handleChange: handleDateChange }}
                            />
                        </div>
                    </FormSectionComponent>

                    <FormSectionComponent config={{ title: 'Pricing' }}>
                        <div className="form-fields form-fields--row">
                            <FieldTextComponent
                                value={_state.get('priceOriginal')}
                                config={{ name: 'priceOriginal', label: 'Original Price', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('priceOriginal', filterNumber(val)) }}
                            />

                            <FieldTextComponent
                                value={_state.get('priceFinal')}
                                config={{ name: 'priceFinal', label: 'Final Price', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('priceFinal', filterNumber(val)) }}
                            />
                        </div>
                    </FormSectionComponent>

                    <FormSectionComponent config={{ title: 'Coupon' }}>
                        <div className="form-fields">
                            <div className="createDealForm-toggleField">
                                <label className="createDealForm-fieldLabel">Type</label>
                                <FieldToggleGroupComponent
                                    value={_clickableCouponState.get('selected')}
                                    config={{ options: couponTypeOptions }}
                                    events={{ handleChange: (val) => _clickableCouponState.set('selected', val) }}
                                />
                            </div>

                            {_clickableCouponState.get('selected') === 1 ? (
                                <FieldTextComponent
                                    value={_clickableCouponState.get('couponClickablePercentage')}
                                    config={{ name: 'couponClickablePercentage', label: 'Percentage Off', errors: config.errors }}
                                    events={{ handleChange: (val) => _clickableCouponState.set('couponClickablePercentage', filterNumber(val)) }}
                                />
                            ) : null}

                            {_clickableCouponState.get('selected') === 2 ? (
                                <FieldTextComponent
                                    value={_clickableCouponState.get('couponClickableAmount')}
                                    config={{ name: 'couponClickableAmount', label: 'Dollars Off', errors: config.errors }}
                                    events={{ handleChange: (val) => _clickableCouponState.set('couponClickableAmount', filterNumber(val)) }}
                                />
                            ) : null}

                            <FieldTextComponent
                                value={_state.get('couponRedeemableDescription')}
                                config={{ name: 'couponRedeemableDescription', label: 'Redeemable Coupon Description', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('couponRedeemableDescription', val) }}
                            />

                            <FieldTextComponent
                                value={_state.get('couponPromoCode')}
                                config={{ name: 'couponPromoCode', label: 'Promo Code', errors: config.errors }}
                                events={{ handleChange: (val) => _state.set('couponPromoCode', val) }}
                            />
                        </div>
                    </FormSectionComponent>

                    <FormSectionComponent config={{ title: 'Media' }}>
                        <FieldTextComponent
                            value={_state.get('imageUrl')}
                            config={{ name: 'imageUrl', label: 'Image URL', errors: config.errors }}
                            events={{ handleChange: (val) => _state.set('imageUrl', val) }}
                        />

                        {_state.get('imageUrl') ? (
                            <div className="createDealForm-image" style={{ backgroundImage: `url(${_state.get('imageUrl')})`}}></div>
                        ) : null}
                    </FormSectionComponent>

                    <div className="form-controls">
                        <ButtonComponent events={{ handleClick: handleSubmit }}>
                            {config.actionType === 'edit' ? 'Save Changes' : 'Create'}
                        </ButtonComponent>
                    </div>
                </div>

                <details className="createDealForm-preview" open>
                    <summary className="createDealForm-previewSummary">Live Preview</summary>
                    <div className="createDealForm-previewHint">What shoppers see on fycdeals.com</div>
                    <DealPreviewCardComponent
                        value={_state.get()}
                        config={{ affiliateName: getSelectedAffiliateName() }}
                    />
                </details>
            </div>
        </div>
    )
}

export { CreateDealFormComponent }
